import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { useOptionalConditionalFormat } from './ConditionalFormatContext'

export function DataTableCard({
  caption,
  toolbarExtra,
  fillViewport = false,
  embedded = false,
  children,
}: {
  caption?: ReactNode
  toolbarExtra?: ReactNode
  /** Grow scroll area to fill a flex parent; omit for short tables (content height). */
  fillViewport?: boolean
  /** Inside `.page-content-stack` — stack owns the outer border/radius. */
  embedded?: boolean
  children: ReactNode
}) {
  const ctx = useOptionalConditionalFormat()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrolling, setScrolling] = useState(false)
  const [canScroll, setCanScroll] = useState(false)
  const [atBottom, setAtBottom] = useState(true)

  const measureScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const overflow = el.scrollHeight > el.clientHeight + 1
    const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
    setCanScroll(overflow)
    setAtBottom(bottom)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let hideTimer: ReturnType<typeof setTimeout>

    const onScroll = () => {
      setScrolling(true)
      measureScroll()
      clearTimeout(hideTimer)
      hideTimer = setTimeout(() => setScrolling(false), 700)
    }

    measureScroll()
    const resizeObserver = new ResizeObserver(measureScroll)
    resizeObserver.observe(el)
    const mutationObserver = new MutationObserver(measureScroll)
    mutationObserver.observe(el, { childList: true, subtree: true })

    el.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', measureScroll)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', measureScroll)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      clearTimeout(hideTimer)
    }
  }, [measureScroll])

  const showBottomFade = canScroll && !atBottom

  return (
    <div
      className={[
        'table-shell flex flex-col overflow-hidden bg-white',
        embedded
          ? fillViewport
            ? 'min-h-0 flex-1 rounded-none border-0 shadow-none'
            : 'shrink-0 rounded-none border-0 shadow-none'
          : fillViewport
            ? 'min-h-0 flex-1 rounded-2xl border border-slate-200 shadow-sm'
            : 'rounded-2xl border border-slate-200 shadow-sm',
      ].join(' ')}
    >
      <div className="table-shell-toolbar z-10 flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2">
        <div className="min-w-0 flex-1 text-xs font-medium text-slate-600">{caption}</div>
        <div className="flex flex-wrap items-center gap-3">
          {toolbarExtra}
          {ctx && (
            <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={ctx.enabled}
                onChange={(e) => ctx.setEnabled(e.target.checked)}
              />
              Color scale
            </label>
          )}
        </div>
      </div>
      <div className={fillViewport ? 'relative min-h-0 flex-1' : 'relative'}>
        <div
          ref={scrollRef}
          className={[
            'table-scroll overflow-auto',
            fillViewport ? 'h-full min-h-[12rem]' : 'max-h-[min(70vh,720px)]',
            scrolling ? 'is-scrolling' : '',
            canScroll ? 'can-scroll' : '',
          ].join(' ')}
        >
          {children}
        </div>
        {showBottomFade && (
          <div
            className="table-shell-bottom-fade pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-white"
            aria-hidden
          />
        )}
      </div>
    </div>
  )
}
